/**
 * Pure JWT-payload decode helper (WI 5160 — F-AUTH-SEAM FE face).
 *
 * Extracts and JSON-parses the middle (payload) segment of a compact JWT.
 * URL-safe base64url characters (`-` → `+`, `_` → `/`) are normalised and
 * missing `=` padding is restored before passing to `atob`.
 *
 * This helper is intentionally side-effect-free and React-free so it can be
 * unit-tested without a DOM environment.
 *
 * **Security note:** this function does NOT validate the JWT signature —
 * the host already validated the token at mint time. The FE only reads claims.
 *
 * @param token A compact-serialised JWT (`header.payload.signature`) or null.
 * @returns The parsed payload object, or `{}` if the token is absent, malformed,
 *          has fewer than 3 segments, or the payload is not valid JSON.
 */
export function decodeJwtPayload(token: string | null): Record<string, unknown>
{
    if (token === null || token === "")
    {
        return {};
    }

    const segments = token.split(".");
    if (segments.length < 3)
    {
        return {};
    }

    const payloadSegment = segments[1];

    try
    {
        // Convert base64url → standard base64 and restore padding.
        const base64 = payloadSegment
            .replace(/-/g, "+")
            .replace(/_/g, "/");

        // Restore `=` padding so atob does not throw.
        const padded = base64 + "===".slice(0, (4 - (base64.length % 4)) % 4);

        const json = atob(padded);
        const parsed: unknown = JSON.parse(json);

        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))
        {
            return {};
        }

        return parsed as Record<string, unknown>;
    }
    catch
    {
        return {};
    }
}
